import { Button, Group, Stack, Textarea, TextInput } from "@mantine/core";
import { useCallback, useMemo } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useForm } from "@mantine/form";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { z } from "zod";
import { emailSchema } from "../../../utils/schema";
import { useNavigate } from "@tanstack/react-router";
import { assertAccountSelected, useUser } from "../../../hooks/user";
import { useTRPC } from "../../../trpc";
import { useMutation } from "@tanstack/react-query";
import { useBasePath } from "../../../hooks/router";
import type { IntlShape } from "react-intl";
import { useMapkaNotification } from "../../../hooks/notifications.js";

interface FormValues {
  email: string;
  name: string;
  companyName: string;
  message: string;
  phone: string;
}

export const messageSchema = (intl: IntlShape) => z.object({
  email: emailSchema(intl),
  name: z.string({
    message: intl.formatMessage({
      id: "error.nameRequired",
      defaultMessage: "Name is required",
    }),
  }),
  companyName: z.string({
    message: intl.formatMessage({
      id: "error.companyNameRequired",
      defaultMessage: "Company name is required",
    }),
  }),
  message: z
    .string({
      message: intl.formatMessage({
        id: "error.messageRequired",
        defaultMessage: "Message is required",
      }),
    })
    .min(10, intl.formatMessage({
      id: "error.messageTooShort10",
      defaultMessage: "Message must be at least 10 characters",
    })),
});

export const ContactSalesForm = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const user = useUser();
  const basePath = useBasePath();
  const mapkaNotification = useMapkaNotification();

  assertAccountSelected(user);
  const {
    email,
    name,
    currentAccount: {
      name: companyName,
    },
  } = user;

  const { mutateAsync: contactSales, isPending } = useMutation(trpc.contactSales.mutationOptions());

  const validate = useMemo(() => zod4Resolver(messageSchema(intl)), [intl]);
  const { onSubmit, getInputProps, reset } = useForm({
    validateInputOnBlur: true,
    initialValues: {
      name,
      email,
      companyName,
      message: "",
      phone: "",
    },
    validate: validate,
  });

  const handleSubmit = useCallback((form: FormValues) => {
    void contactSales(form).then(() => {
      mapkaNotification({
        variant: "success",
        title: intl.formatMessage({
          defaultMessage: "Message sent",
          id: "notification.messageSent",
        }),
        message: intl.formatMessage({
          defaultMessage: "Your message has been sent",
          id: "notification.messageSentDescription",
        }),
        color: "green",
      });
      reset();
    });
  }, [contactSales, reset, intl]);

  const handleCancel = useCallback(() => {
    reset();
    void navigate({ to: basePath });
  }, [reset]);

  return (
    <form onSubmit={onSubmit(handleSubmit)}>
      <Stack gap="xs">
        <TextInput
          data-autofocus
          disabled={isPending}
          label={intl.formatMessage({ defaultMessage: "Name", id: "label.name" })}
          {...getInputProps("name")}
        />
        <TextInput
          disabled={isPending}
          label={intl.formatMessage({ defaultMessage: "Company name", id: "label.companyName" })}
          {...getInputProps("companyName")}
        />
        <TextInput
          disabled={isPending}
          label={intl.formatMessage({ defaultMessage: "Email address", id: "label.emailAddress" })}
          {...getInputProps("email")}
        />
        <TextInput
          disabled={isPending}
          type="tel"
          placeholder="+1 (222) 222-2222"
          label={intl.formatMessage({ defaultMessage: "Phone", id: "label.phone" })}
          {...getInputProps("phone")}
        />
        <Textarea
          disabled={isPending}
          rows={7}
          label={intl.formatMessage({ defaultMessage: "Message", id: "label.message" })}
          {...getInputProps("message")}
        />
      </Stack>
      <Group gap="xs" justify="flex-end" pt="lg">
        <Button
          type="reset"
          variant="default"
          loading={isPending}
          onClick={handleCancel}
        >
          <FormattedMessage id="button.cancel" defaultMessage="Cancel" />
        </Button>

        <Button
          variant="tertiary"
          type="submit"
          loading={isPending}
        >
          <FormattedMessage id="button.send" defaultMessage="Send" />
        </Button>
      </Group>
    </form>
  );
};
